import type React from "react";

/**
 * Parses a CSS string of custom property declarations into a React-compatible
 * style object so they can be applied as an inline style on the root element.
 *
 * Only `--gc-*` properties are accepted; any other declarations are ignored to
 * prevent the venue from accidentally overriding structural styles.
 *
 * @example
 *   parseCssVariables("--gc-color-primary: #C0392B; --gc-border-radius-button: 4px;")
 *   // → { "--gc-color-primary": "#C0392B", "--gc-border-radius-button": "4px" }
 */
export function parseCssVariables(theme: string | undefined): React.CSSProperties | undefined {
  if (!theme) return undefined;

  const result: Record<string, string> = {};

  for (const declaration of theme.split(";")) {
    const colonIndex = declaration.indexOf(":");
    if (colonIndex === -1) continue;

    const property = declaration.slice(0, colonIndex).trim();
    const value = declaration.slice(colonIndex + 1).trim();

    if (property.startsWith("--gc-") && value) {
      result[property] = value;
    }
  }

  return Object.keys(result).length > 0 ? (result as React.CSSProperties) : undefined;
}

/**
 * Returns true when a value looks like a `--gc-*` declaration list rather than
 * a keyword. Used where an attribute now carries a keyword but earlier releases
 * accepted CSS custom properties, so both spellings keep working.
 */
export function hasCssVariableDeclarations(value: string | undefined): boolean {
  return !!value && value.includes("--gc-") && value.includes(":");
}
